import {Injectable, OnDestroy} from '@angular/core';
import {Observable} from 'rxjs';
import {CdNotification, isLink} from '../../root-store/notification/notification.reducer';

type BroadcastMessage = RemoveNotification | RemoveAllNotifications;

type SerializedNotification = Omit<CdNotification, 'createdTs' | 'id'>;

interface RemoveNotification {
  type: 'removeNotification';
  notification: SerializedNotification;
}

interface RemoveAllNotifications {
  type: 'removeAllNotifications';
}

@Injectable({
  providedIn: 'root'
})
export class NotificationBroadcastService implements OnDestroy {
  readonly channel: BroadcastChannel;

  static serialize(notification: CdNotification): SerializedNotification {
    return {
      title: notification.title,
      message: notification.message,
      type: notification.type,
      removeAfterSec: notification.removeAfterSec,
      // for ngrx actions, we only include the type, because the payload might other non-serializable data and the label is enough to identify the action
      directActions: notification.directActions?.map(a => ({
        label: a.label,
        actions: isLink(a.actions) ? a.actions : a.actions.map(action => ({type: action.type}))
      }))
    };
  }

  constructor() {
    this.channel = new BroadcastChannel('notifications');
  }

  public broadcastRemoveNotification(notification: CdNotification): void {
    const notificationWithRelevantAttributesOnly = NotificationBroadcastService.serialize(notification);
    this.channel.postMessage({
      type: 'removeNotification',
      notification: notificationWithRelevantAttributesOnly
    });
  }

  public broadcastRemoveAllNotifications(): void {
    this.channel.postMessage({type: 'removeAllNotifications'});
  }

  public subscribeDeleteAllNotifications(): Observable<void> {
    return new Observable<void>(subscriber => {
      const listener = (event: MessageEvent<BroadcastMessage>) => {
        if (event.data.type === 'removeAllNotifications') {
          subscriber.next();
        }
      };
      this.channel.addEventListener('message', listener);
      return () => this.channel.removeEventListener('message', listener);
    });
  }

  public subscribeDeleteNotification(): Observable<SerializedNotification> {
    return new Observable<SerializedNotification>(subscriber => {
      const listener = (event: MessageEvent<BroadcastMessage>) => {
        if (event.data.type === 'removeNotification') {
          subscriber.next(event.data.notification);
        }
      };
      this.channel.addEventListener('message', listener);
      return () => this.channel.removeEventListener('message', listener);
    });
  }

  ngOnDestroy(): void {
    this.channel.close();
  }
}
